import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {
  useListBlogCategoriesQuery,
  useListBlogArticlesQuery,
  useGetBlogArticleBySlugQuery,
} from '../store/siteContentApi';
import { renderBlogMarkdown } from '../utils/blogMarkdown';
import { BlogShimmer } from '../components/shimmer';
import { useCart } from '../context/CartContext';
import { useUI } from '../context/UIContext';
import { ROUTES, buildRoute } from '../constants/routes';

/* ───────── inline style objects (mirrors the HTML CSS exactly) ───────── */
const font = {
  bebas: { fontFamily: "'Bebas Neue', sans-serif" },
  playfair: { fontFamily: "'Playfair Display', 'Cormorant Garamond', serif" },
  mono: { fontFamily: "'Space Mono', 'JetBrains Mono', monospace" },
  dm: { fontFamily: "'DM Sans', 'Outfit', sans-serif" },
};

const colors = {
  greenDark: '#0A2218',
  greenMid: '#0F3525',
  offWhite: '#F4F1EC',
  warmGrey: '#C8C4BA',
  bodyText: '#2A2A2A',
  teal: '#00B5AD',
  tealDark: '#007A75',
  tealLight: '#4DD9D4',
  yellow: '#F5C518',
  border: '#E0DDD6',
  card: '#FFFFFF',
};

/* ═══════════════════════════════════════════════════════════════
   BLOG PAGE
   ═══════════════════════════════════════════════════════════════ */
const Blog = () => {
  const { slug: urlSlug } = useParams();
  const navigate = useNavigate();
  const [activeCat, setActiveCat] = useState('all');
  const [modalSlug, setModalSlug] = useState(urlSlug || null);

  const { data: rawCategories = [], isLoading: catsLoading } = useListBlogCategoriesQuery();
  const { data: rawArticles = [], isLoading: articlesLoading } = useListBlogArticlesQuery();
  const isLoading = catsLoading || articlesLoading;

  // Prepend the synthetic "All Articles" tab — same shape as the real ones.
  const categories = useMemo(
    () => [{ slug: 'all', label: 'All Articles' }, ...rawCategories],
    [rawCategories],
  );

  const catBySlug = useMemo(
    () => Object.fromEntries(rawCategories.map((c) => [c.slug, c])),
    [rawCategories],
  );

  const articlesByCat = useCallback(
    (catSlug) => rawArticles.filter((a) => a.category_slug === catSlug),
    [rawArticles],
  );

  const catMeta = useCallback((catSlug) => {
    if (catSlug === 'all') return { slug: 'all', label: 'All Articles' };
    return catBySlug[catSlug] || {};
  }, [catBySlug]);

  // Sync modal ↔ URL: opening updates URL, closing clears it.
  useEffect(() => {
    setModalSlug(urlSlug || null);
  }, [urlSlug]);

  const openModal = useCallback((slug) => {
    setModalSlug(slug);
    navigate(`/blog/${slug}`, { replace: false });
  }, [navigate]);

  const closeModal = useCallback(() => {
    setModalSlug(null);
    navigate('/blog', { replace: false });
  }, [navigate]);

  // close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [closeModal]);

  return (
    <Box sx={{ ...font.dm, bgcolor: colors.offWhite, color: colors.bodyText, minHeight: '100vh' }}>

      {/* ── HERO ── */}
      <Box sx={{ bgcolor: colors.greenMid, py: { xs: 8, md: 10 }, px: { xs: 3, md: 6 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -60, right: -80, width: 500, height: 500, background: 'radial-gradient(circle, rgba(0,181,173,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Typography sx={{ ...font.mono, fontSize: 10, letterSpacing: 4, color: colors.tealLight, textTransform: 'uppercase', mb: 2.5 }}>
            Health Intelligence Blog Series
          </Typography>

          <Typography sx={{ ...font.bebas, fontSize: { xs: 45, md: 75 }, color: '#fff', lineHeight: 0.92, letterSpacing: 1, mb: 3 }}>
            Know Your<br />
            <Box component="em" sx={{ color: colors.yellow, fontStyle: 'normal' }}>Numbers.</Box>
          </Typography>

          <Typography sx={{ fontSize: 16, color: colors.warmGrey, maxWidth: 560, lineHeight: 1.7, fontWeight: 300 }}>
            Science-backed, clinically precise articles that translate your diagnostic results into decisions. Because every number in your report tells a story.
          </Typography>

          {/* Stats row — auto-derived from API */}
          <Box sx={{ display: 'flex', gap: 5, mt: 6, pt: 5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            {[
              [String(rawArticles.length || '—'), 'Articles'],
              [String(rawCategories.length || '—'), 'Categories'],
              ['∞', 'Health Intelligence'],
            ].map(([val, label]) => (
              <Box key={label} sx={{ textAlign: 'left' }}>
                <Typography sx={{ ...font.bebas, fontSize: 36, color: colors.tealLight, lineHeight: 1 }}>{val}</Typography>
                <Typography sx={{ ...font.mono, fontSize: 9, color: colors.warmGrey, letterSpacing: 2, textTransform: 'uppercase', mt: 0.5 }}>{label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── CATEGORY NAV ── */}
      <Box sx={{ bgcolor: colors.greenDark, px: { xs: 2, md: 6 }, display: 'flex', gap: 0, borderBottom: '2px solid rgba(0,181,173,0.2)', overflowX: 'auto' }}>
        {categories.map((c) => {
          const active = activeCat === c.slug;
          return (
            <Box
              key={c.slug}
              onClick={() => setActiveCat(c.slug)}
              sx={{
                ...font.mono,
                fontSize: 10,
                letterSpacing: 2,
                textTransform: 'uppercase',
                color: active ? colors.yellow : colors.warmGrey,
                bgcolor: 'transparent',
                border: 'none',
                py: 2.25,
                px: 3,
                cursor: 'pointer',
                borderBottom: `2px solid ${active ? colors.yellow : 'transparent'}`,
                mb: '-2px',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
                '&:hover': { color: '#fff' },
                userSelect: 'none',
              }}
            >
              {c.label}
            </Box>
          );
        })}
      </Box>

      {/* ── SECTIONS ── */}
      {isLoading ? (
        <BlogShimmer columns={3} />
      ) : activeCat === 'all' ? (
        <AllArticlesSection
          openModal={openModal}
          categories={rawCategories}
          articlesByCat={articlesByCat}
        />
      ) : (
        <CategorySection
          catSlug={activeCat}
          openModal={openModal}
          articles={articlesByCat(activeCat)}
          cat={catMeta(activeCat)}
        />
      )}

      {/* ── ARTICLE MODAL ── */}
      <ArticleModal slug={modalSlug} catBySlug={catBySlug} onClose={closeModal} />

      {/* ── FOOTER ── */}
      <Box sx={{ bgcolor: colors.greenDark, py: 5, px: { xs: 3, md: 6 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid rgba(0,181,173,0.2)', mt: 5 }}>
        <Typography sx={{ ...font.playfair, color: 'rgba(255,255,255,0.5)', fontSize: 16 }}>TrustLab Diagnostics</Typography>
        <Typography sx={{ ...font.mono, fontSize: 8, color: 'rgba(255,255,255,0.25)', letterSpacing: 2, textTransform: 'uppercase' }}>Know Your Numbers · Health Intelligence Series</Typography>
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════
   ALL ARTICLES SECTION (3-column index view)
   ═══════════════════════════════════════════════════════════════ */
const AllArticlesSection = ({ openModal, categories, articlesByCat }) => {
  return (
    <Box sx={{ py: 8, px: { xs: 3, md: 6 }, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 4 }}>
        {categories.map((cat) => {
          const articles = articlesByCat(cat.slug);
          return (
            <Box key={cat.slug}>
              <Typography sx={{ ...font.bebas, fontSize: 22, letterSpacing: 1, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, color: cat.color }}>
                {cat.icon} {cat.label.replace(/^[^\s]+\s/, '')}
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {articles.map((a, i) => (
                  <Box
                    component="li"
                    key={a.slug}
                    onClick={() => openModal(a.slug)}
                    sx={{
                      borderBottom: '1px solid var(--border, #E0DDD6)',
                      py: 1.25,
                      cursor: 'pointer',
                      transition: 'padding-left 0.2s',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.25,
                      '&:hover': { pl: 0.75 },
                    }}
                  >
                    <Typography sx={{ ...font.mono, fontSize: 9, color: '#bbb', pt: 0.25, flexShrink: 0, width: 18 }}>
                      {String(i + 1).padStart(2, '0')}
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: colors.bodyText, lineHeight: 1.4 }}>
                      {a.title}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════
   CATEGORY SECTION (featured card + grid)
   ═══════════════════════════════════════════════════════════════ */
const CategorySection = ({ catSlug, openModal, articles, cat }) => {
  if (!articles.length) return null;

  const iconBg = {
    diabetes: 'rgba(220,76,43,0.12)',
    cardiac: 'rgba(176,30,60,0.12)',
    bp: 'rgba(13,92,148,0.12)',
    hormones: 'rgba(102,40,132,0.12)',
    resp: 'rgba(0,124,112,0.12)',
  };

  return (
    <Box sx={{ py: 8, px: { xs: 3, md: 6 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Section header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 3, mb: 6, pb: 3, borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap' }}>
        <Box sx={{ width: 56, height: 56, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0, bgcolor: iconBg[catSlug] || 'rgba(0,0,0,0.06)' }}>
          {cat.icon}
        </Box>
        <Box>
          <Typography sx={{ ...font.mono, fontSize: 9, letterSpacing: 3, color: cat.color || colors.teal, textTransform: 'uppercase', mb: 0.5 }}>Category</Typography>
          <Typography sx={{ ...font.bebas, fontSize: 42, lineHeight: 1, color: cat.color || colors.greenDark }}>{cat.label?.replace(/^[^\s]+\s/, '')}</Typography>
        </Box>
      </Box>

      {/* Blog grid — first card is featured (wide), rest are normal */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
        {articles.map((a, i) => (
          <ArticleCard key={a.slug} article={a} featured={i === 0} openModal={openModal} cat={cat} />
        ))}
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════
   ARTICLE CARD
   ═══════════════════════════════════════════════════════════════ */
const ArticleCard = ({ article, featured, openModal, cat }) => {
  const a = article;
  const catInfo = cat || {};
  const cardGradient = a.gradient || catInfo.gradient || 'linear-gradient(135deg, #3D7A52 0%, #1E3D2A 100%)';

  return (
    <Box
      onClick={() => openModal(a.slug)}
      sx={{
        bgcolor: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: '4px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        display: 'flex',
        flexDirection: featured ? { xs: 'column', md: 'row' } : 'column',
        gridColumn: featured ? { md: '1 / -1' } : undefined,
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 40px rgba(0,0,0,0.1)' },
      }}
    >
      {/* Visual */}
      <Box
        sx={{
          background: cardGradient,
          height: featured ? { xs: 200, md: '100%' } : 180,
          minHeight: featured ? { md: 240 } : undefined,
          width: featured ? { md: 340 } : '100%',
          flexShrink: 0,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Tag */}
        {a.featured && (
          <Box sx={{ position: 'absolute', top: 14, left: 14, ...font.mono, fontSize: 8, letterSpacing: 2, textTransform: 'uppercase', px: 1.25, py: 0.5, borderRadius: '2px', bgcolor: 'rgba(0,0,0,0.5)', color: '#fff', backdropFilter: 'blur(4px)' }}>
            Featured
          </Box>
        )}
        {/* Number overlay */}
        <Typography sx={{ position: 'absolute', bottom: 12, right: 14, ...font.bebas, fontSize: 64, lineHeight: 1, color: 'rgba(255,255,255,0.12)', letterSpacing: -2 }}>
          {a.num}
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ p: featured ? { xs: 3, md: 5 } : 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography sx={{ ...font.mono, fontSize: 9, letterSpacing: 2, color: '#999', textTransform: 'uppercase', mb: 1.25 }}>
          {catInfo.label?.replace(/^[^\s]+\s/, '')} · {a.read_time}
        </Typography>

        <Typography sx={{ ...font.playfair, fontSize: featured ? { xs: 18, md: 26 } : 18, fontWeight: 700, lineHeight: 1.35, color: colors.greenDark, mb: 1.5 }}>
          {a.title}
        </Typography>

        <Typography sx={{ fontSize: 13.5, color: '#555', lineHeight: 1.7, fontWeight: 300, mb: 2.5, flex: 1 }}>
          {a.excerpt}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 2, borderTop: `1px solid ${colors.border}` }}>
          <Typography sx={{ ...font.mono, fontSize: 9, color: '#aaa', letterSpacing: 1 }}>{a.read_time}</Typography>
          <Typography sx={{ ...font.mono, fontSize: 9, letterSpacing: 1.5, textTransform: 'uppercase', color: colors.tealDark, display: 'flex', alignItems: 'center', gap: 0.75, transition: 'gap 0.2s', '&:hover': { gap: 1.25 } }}>
            Read Article →
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════════════
   ARTICLE MODAL (full-screen dialog) — fetches the full body by slug
   ═══════════════════════════════════════════════════════════════ */
const ArticleModal = ({ slug, catBySlug, onClose }) => {
  const navigate = useNavigate();
  const { data: a } = useGetBlogArticleBySlugQuery(slug, { skip: !slug });
  const bodyHtml = useMemo(() => renderBlogMarkdown(a?.body_markdown), [a?.body_markdown]);
  const { addItem, inCart } = useCart();
  const { showToast } = useUI();

  // Resolve the linked-tests list. Backend serves cta_tests pre-normalised
  // (legacy single cta_test promoted to a 1-item array), but we keep the
  // chain client-side too so any cached pre-upgrade response still renders.
  const linkedTests = useMemo(() => {
    if (!a) return [];
    if (Array.isArray(a.cta_tests) && a.cta_tests.length) return a.cta_tests;
    if (a.cta_test?.test_id) return [a.cta_test];
    return [];
  }, [a]);

  // The reader can deselect any of the article's linked tests before
  // adding. This is local UI state — the article's own cta_tests stay
  // untouched in the database. Reset whenever the user opens a new
  // article so the previous deselections don't carry over.
  const [selectedIds, setSelectedIds] = useState([]);
  useEffect(() => {
    setSelectedIds(linkedTests.map((t) => t.test_id));
  }, [linkedTests]);

  const dropTest = (id) => setSelectedIds((ids) => ids.filter((x) => x !== id));

  // "Add all selected to cart" — loops the user's current selection,
  // skips items already in the cart (so re-clicking is idempotent),
  // toasts ONCE with a combined message, then navigates straight to
  // /cart. We intentionally don't call onClose() first — the parent
  // Blog page's closeModal also navigates, and the two same-tick
  // navigations can race. Going to /cart unmounts the Dialog anyway.
  const onAddSelected = () => {
    const picks = linkedTests.filter((t) => selectedIds.includes(t.test_id));
    if (picks.length === 0) return;
    let added = 0;
    try {
      picks.forEach((t) => {
        if (!inCart(t.test_id, 'test')) {
          addItem({
            id:        t.test_id,
            type:      'test',
            name:      t.test_name,
            price:     Number(t.mrp) || 0,
            original:  null,
            icon:      '🧪',
            tat:       '',
            paramCount: 0,
          });
          added += 1;
        }
      });
      if (added > 0) {
        showToast(
          added === 1
            ? '1 test added to cart'
            : `${added} tests added to cart`,
          'success',
        );
      }
      navigate('/cart');
    } catch {
      showToast('Could not add to cart', 'error');
    }
  };

  if (!slug || !a) return null;
  const catInfo = catBySlug?.[a.category_slug] || {};
  const cardGradient = a.gradient || catInfo.gradient || 'linear-gradient(135deg, #3D7A52 0%, #1E3D2A 100%)';
  const metaLine = `${(catInfo.label || a.category_slug || '').replace(/^[^\s]+\s/, '')} · ${a.read_time || ''}`;

  // CTA button copy — depends on what's already in the cart.
  const allAlreadyInCart = linkedTests.length > 0
    && selectedIds.length > 0
    && linkedTests
        .filter((t) => selectedIds.includes(t.test_id))
        .every((t) => inCart(t.test_id, 'test'));
  const ctaLabel = selectedIds.length === 0
    ? 'Select at least one test'
    : allAlreadyInCart
      ? 'Go to cart →'
      : selectedIds.length === 1
        ? 'Book test at Trustlab'
        : `Book ${selectedIds.length} tests at Trustlab`;

  return (
    <Dialog
      open={Boolean(slug)}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', maxWidth: '100vw', m: 0, overflow: 'visible' } }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' } } }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          justifyContent: 'center',
          py: { xs: 2, md: 5 },
          px: { xs: 1, md: 5 },
          overflowY: 'auto',
        },
      }}
    >
      <Box sx={{ bgcolor: '#fff', maxWidth: 1100, width: '100%', borderRadius: '4px', maxHeight: '90vh', overflowY: 'auto', animation: 'slideUp 0.3s ease', '@keyframes slideUp': { from: { transform: 'translateY(30px)', opacity: 0 }, to: { transform: 'translateY(0)', opacity: 1 } } }}>

        {/* Hero banner */}
        <Box sx={{ height: 220, background: cardGradient, position: 'relative', display: 'flex', alignItems: 'flex-end', p: 3.5 }}>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute', top: 16, right: 16, width: 36, height: 36,
              bgcolor: 'rgba(0,0,0,0.4)', color: '#fff', backdropFilter: 'blur(4px)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box sx={{ ...font.mono, fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', py: 0.625, px: 1.5, borderRadius: '2px', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)' }}>
            {catInfo.label?.replace(/^[^\s]+\s/, '')}
          </Box>
        </Box>

        {/* Body */}
        <Box sx={{ px: { xs: 3, md: 5.5 }, py: { xs: 4, md: 5 } }}>
          <Typography sx={{ ...font.playfair, fontSize: { xs: 22, md: 28 }, fontWeight: 700, lineHeight: 1.3, color: colors.greenDark, mb: 1 }}>
            {a.title}
          </Typography>
          <Typography sx={{ ...font.mono, fontSize: 9, color: '#aaa', letterSpacing: 2, textTransform: 'uppercase', mb: 3.5, pb: 2.5, borderBottom: `1px solid ${colors.border}` }}>
            {metaLine}
          </Typography>

          {/* Article content — rendered from markdown (HTML pass-through supported) */}
          <Box
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
            sx={{
              '& h3': { ...font.dm, fontSize: 14, fontWeight: 600, color: colors.greenDark, textTransform: 'uppercase', letterSpacing: 1, mt: 3.5, mb: 1.25 },
              '& p': { fontSize: 15, lineHeight: 1.8, color: '#444', fontWeight: 300, mb: 2 },
              '& em': { fontStyle: 'italic' },
              '& .highlight-box': { bgcolor: colors.greenDark, color: '#fff', p: '20px 24px', borderRadius: '4px', my: 3, borderLeft: `3px solid ${colors.teal}` },
              '& .highlight-box .hl-label': { ...font.mono, fontSize: 9, letterSpacing: 2, color: colors.tealLight, textTransform: 'uppercase', mb: 0.75 },
              '& .highlight-box p': { color: colors.warmGrey, fontSize: 14, m: 0 },
              '& .numbers-row': { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, my: 3 },
              '& .num-box': { bgcolor: colors.offWhite, border: `1px solid ${colors.border}`, borderRadius: '4px', p: 2, textAlign: 'center' },
              '& .num-box .nb-val': { ...font.bebas, fontSize: 28, lineHeight: 1, color: colors.greenDark },
              '& .num-box .nb-label': { ...font.mono, fontSize: 8, color: '#999', letterSpacing: 1, mt: 0.5, textTransform: 'uppercase' },
            }}
          />

          {/* CTA panel — the reader sees every test the admin linked,
              can tap the ✕ on any they don't want, and then hits ONE
              button that bulk-adds the rest and jumps to /cart.
              Hidden entirely when the admin didn't link any tests. */}
          {linkedTests.length > 0 && (
            <Box sx={{ mt: 4.5, pt: 3.5, borderTop: `1px solid ${colors.border}` }}>
              <Typography sx={{ ...font.mono, fontSize: 9, letterSpacing: 2, color: colors.tealDark, textTransform: 'uppercase', mb: 2 }}>
                Tests mentioned in this article
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2.5 }}>
                {linkedTests
                  .filter((t) => selectedIds.includes(t.test_id))
                  .map((t) => {
                    const alreadyIn = inCart(t.test_id, 'test');
                    return (
                      <Box
                        key={t.test_id}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          bgcolor: colors.offWhite,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '4px',
                          p: 1.75,
                        }}
                      >
                        <Box sx={{
                          width: 36, height: 36, borderRadius: '50%',
                          bgcolor: '#fff', border: `1px solid ${colors.border}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 18, flexShrink: 0,
                        }}>
                          🧪
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: colors.greenDark }} noWrap>
                            {t.test_name || 'Test'}
                          </Typography>
                          <Typography sx={{ ...font.mono, fontSize: 10, color: '#888', letterSpacing: 0.5 }}>
                            {t.test_code || `ID ${t.test_id}`}
                            {t.specialisation ? ` · ${t.specialisation}` : ''}
                            {alreadyIn ? ' · already in cart' : ''}
                          </Typography>
                        </Box>
                        {Number(t.mrp) > 0 && (
                          <Typography sx={{ ...font.playfair, fontSize: 15, fontWeight: 700, color: colors.greenDark, flexShrink: 0 }}>
                            ₹{Number(t.mrp).toLocaleString('en-IN')}
                          </Typography>
                        )}
                        {linkedTests.length > 1 && (
                          <Box
                            component="button"
                            onClick={() => navigate(buildRoute(ROUTES.TEST_DETAIL, { id: t.test_id }))}
                            sx={{
                              ...font.mono, fontSize: 9, letterSpacing: 1.5, color: colors.tealDark,
                              bgcolor: 'transparent', border: 'none', cursor: 'pointer',
                              textDecoration: 'underline', textUnderlineOffset: 3, flexShrink: 0,
                              '&:hover': { color: colors.greenDark },
                            }}
                          >
                            View Details
                          </Box>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => dropTest(t.test_id)}
                          aria-label={`Remove ${t.test_name || 'this test'}`}
                          sx={{ color: '#999', '&:hover': { color: '#DC2626' } }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box
                  component="button"
                  onClick={selectedIds.length === 0
                    ? undefined
                    : (allAlreadyInCart ? () => navigate('/cart') : onAddSelected)}
                  disabled={selectedIds.length === 0}
                  sx={{
                    ...font.mono, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
                    bgcolor: selectedIds.length === 0 ? '#B8B8B0' : colors.greenDark,
                    color: '#fff', border: 'none', borderRadius: '3px',
                    py: 1.75, px: 3,
                    cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer',
                    transition: 'background 0.2s',
                    '&:hover': selectedIds.length === 0 ? undefined : { bgcolor: colors.tealDark },
                  }}
                >
                  {ctaLabel}
                </Box>
                {selectedIds.length === 1 && (() => {
                  const singleTest = linkedTests.find((t) => t.test_id === selectedIds[0]);
                  return singleTest ? (
                    <Box
                      component="button"
                      onClick={() => navigate(buildRoute(ROUTES.TEST_DETAIL, { id: singleTest.test_id }))}
                      sx={{
                        fontFamily: "'DM Sans', 'Outfit', sans-serif", fontSize: 13, fontWeight: 500,
                        color: colors.teal, bgcolor: '#fff',
                        border: `1.5px solid ${colors.teal}`, borderRadius: '4px',
                        py: 1.625, px: 2.5, cursor: 'pointer',
                        transition: 'background 0.2s, color 0.2s',
                        '&:hover': { bgcolor: colors.teal, color: '#fff' },
                      }}
                    >
                      View Test Details
                    </Box>
                  ) : null;
                })()}

                {/* Restore-all link, only when the reader has deselected
                    at least one. Lets them undo without reopening the
                    article. */}
                {selectedIds.length < linkedTests.length && (
                  <Box
                    component="button"
                    onClick={() => setSelectedIds(linkedTests.map((t) => t.test_id))}
                    sx={{
                      ...font.mono, fontSize: 10, letterSpacing: 1.5, color: colors.tealDark,
                      bgcolor: 'transparent', border: 'none', cursor: 'pointer',
                      textDecoration: 'underline', textUnderlineOffset: 3,
                      '&:hover': { color: colors.greenDark },
                    }}
                  >
                    Restore all ({linkedTests.length})
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default Blog;
